import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Modal } from "../common/Modal";
import { Button } from "../common/Button";
import { getQrPreferences, QR_SETTINGS_PATH } from "../../services/qrPreferences";
import styles from "./QrCodeLabel.module.css";

const LABEL_SIZES = {
  small: { name: "Small label (2x1in)", qrSize: 72 },
  large: { name: "Large label (4x3in)", qrSize: 160 },
  weatherproof: { name: "Weatherproof label (3x3in)", qrSize: 128 },
};

/**
 * What a label can ask the app to do when scanned (#163).
 *
 * Mirrors HIVE_QR_ACTIONS in mobile/src/services/qrService.js. Duplicated
 * rather than shared because the two apps have no common module, and kept
 * SHORT for that reason: the app is the one that must recognise an action,
 * so an entry added here and not there prints a label that opens the hive
 * and says why it could not do more.
 *
 * `printed` is the line that appears under the hive number in plain text. A
 * label that silently starts something is worse than one that just opens
 * the hive -- somebody has to be able to read what they are about to scan.
 */
const QR_ACTIONS = {
  "": { name: "Just open the hive", printed: null },
  inspect: { name: "Log an inspection", printed: "Scan to log an inspection" },
  feed: { name: "Log a feeding", printed: "Scan to log a feeding" },
  treat: { name: "Log a treatment", printed: "Scan to log a treatment" },
  checklist: { name: "Run the checklist", printed: "Scan to run the checklist" },
};

/** Bare beekeeper://hive/{id} with no action, byte for byte as before --
 *  every label printed so far is that form, and they will outlive several
 *  app versions. */
function qrValue(hiveId, action) {
  return action ? `beekeeper://hive/${hiveId}/${action}` : `beekeeper://hive/${hiveId}`;
}

/** hive: { hive_id, hive_number, site_name } -- one hive, or pass `hives`
 * (array) for bulk label printing. */
export function QrCodeLabel({ hive, hives, trigger }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("warn");
  const [size, setSize] = useState("large");
  const [action, setAction] = useState("");
  const printRef = useRef(null);

  const items = hives || (hive ? [hive] : []);

  // Re-read on every open rather than once at mount: the user can turn the
  // warning off from the link below, and coming back to a still-mounted hive
  // page should respect that without a reload.
  useEffect(() => {
    if (open) setStep(getQrPreferences().showPrintWarning ? "warn" : "print");
  }, [open]);

  function print() {
    const printContents = printRef.current.innerHTML;
    const win = window.open("", "_blank", "width=800,height=600");
    win.document.write(`<html><head><title>Hive Labels</title><style>
      body { font-family: sans-serif; }
      .label { display: inline-block; text-align: center; margin: 8px; padding: 8px; border: 1px dashed #999; page-break-inside: avoid; }
    </style></head><body>${printContents}</body></html>`);
    win.document.close();
    win.focus();
    win.print();
  }

  const showingWarning = step === "warn";

  return (
    <>
      <span onClick={() => setOpen(true)}>{trigger}</span>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={
          showingWarning
            ? "Before you print"
            : items.length > 1
              ? "Bulk Print QR Labels"
              : "Print QR Label"
        }
        footer={
          showingWarning ? (
            <>
              <Link to={QR_SETTINGS_PATH} className={styles.warningFooterNote}>
                Don't show this again
              </Link>
              <Button onClick={() => setStep("print")}>I understand — continue</Button>
            </>
          ) : (
            <Button onClick={print}>Print</Button>
          )
        }
      >
        {showingWarning ? (
          <>
            <ul className={styles.warningList}>
              <li>
                <strong>Weather wrecks paper.</strong> A label on a hive lives
                outdoors for months. Use the weatherproof size on laminated or
                synthetic stock, and keep it out of standing water and direct
                sun where you can.
              </li>
              <li>
                <strong>Check the number before you record anything.</strong>{" "}
                Each label prints its hive number in plain text next to the
                code. If a scan opens a different hive than the one in front of
                you, the label has been moved, swapped, or reprinted — stop and
                fix the label rather than editing the hive it opened.
              </li>
              <li>
                <strong>Anyone can scan a label, and that is fine.</strong> The
                code holds a hive reference only — no password, no access token,
                no location. It opens nothing unless the person scanning is
                already signed in to an account with access to that hive.
              </li>
            </ul>
            <p className={styles.warningFooterNote}>
              Printing {items.length} label{items.length === 1 ? "" : "s"}.
            </p>
          </>
        ) : (
          <>
            <label className={styles.sizeLabel}>
              Label size
              <select id="qr-code-label-size" name="size" value={size} onChange={(e) => setSize(e.target.value)} className={styles.select}>
                {Object.entries(LABEL_SIZES).map(([key, s]) => (
                  <option key={key} value={key}>{s.name}</option>
                ))}
              </select>
            </label>

            <label className={styles.sizeLabel}>
              When scanned
              <select id="qr-code-label-action" name="action" value={action} onChange={(e) => setAction(e.target.value)} className={styles.select}>
                {Object.entries(QR_ACTIONS).map(([key, a]) => (
                  <option key={key || "none"} value={key}>{a.name}</option>
                ))}
              </select>
            </label>
            <p className={styles.warningFooterNote}>
              Scanning still opens a screen to confirm — a label never records
              anything on its own.
            </p>

            <div ref={printRef} className={styles.preview}>
              {items.map((h) => (
                <div className="label" key={h.hive_id}>
                  <QRCodeSVG value={qrValue(h.hive_id, action)} size={LABEL_SIZES[size].qrSize} />
                  <div><strong>Hive {h.hive_number}</strong></div>
                  {h.site_name && <div>{h.site_name}</div>}
                  {/* In plain text, on the label itself. */}
                  {QR_ACTIONS[action].printed && <div style={{ fontSize: "0.75em" }}>{QR_ACTIONS[action].printed}</div>}
                </div>
              ))}
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
